/**
 * Read a meta tag that the backend injected into the SPA shell.
 *
 * backend/pb_hooks/lib/og_inject.js rewrites the `<head>` block per request, so
 * by the time React mounts, the page already contains resource-specific title
 * and description text that the server has resolved AND authorised. Reading it
 * back is strictly better than fetching: several of these records are not
 * publicly readable (messages are crew-gated), so the alternative would be a
 * new anonymous endpoint exposing data the share hook already gates carefully.
 *
 * Returns null when the tag is absent — e.g. `vite preview`, which serves the
 * static shell with no hooks in front of it, or a direct SPA navigation where
 * the head still belongs to the previously-loaded route.
 */
export function useInjectedMeta(property: string): string | null {
  if (typeof document === 'undefined') return null;

  // Callers pass compile-time literals ('og:title', 'og:description'), never
  // user input, so there is nothing to escape. The guard is here so that stays
  // true if someone later threads a variable through — it rejects rather than
  // building a selector out of it. (CSS.escape would be the general answer, but
  // it is not in this project's lint globals and is not needed for constants.)
  if (!/^[a-zA-Z0-9:_-]+$/.test(property)) return null;

  const el =
    document.querySelector(`meta[property="${property}"]`) ??
    document.querySelector(`meta[name="${property}"]`);

  const content = el?.getAttribute('content')?.trim();
  if (!content) return null;

  // The generic site card means "the hook had nothing specific to say", which
  // for a share landing page is the same as having no value at all.
  if (content === "Skiffs — Don't Row Alone" || content === 'Skiffs') return null;

  return content;
}
